import { useRef, useEffect, RefObject } from 'react';

type UsePipelineInfiniteScrollParams = {
  hasMore: boolean;
  isLoading: boolean;
  isInitialLoading: boolean;
  loadMore: () => void;
  threshold?: number;
};

type UsePipelineInfiniteScrollResult = {
  sentinelRef: RefObject<HTMLDivElement | null>;
};

/**
 * Hook for infinite scroll in pipeline columns.
 * Returns a ref to attach to a sentinel element that triggers loadMore when visible.
 */
export const usePipelineInfiniteScroll = ({
  hasMore,
  isLoading,
  isInitialLoading,
  loadMore,
  threshold = 0.1,
}: UsePipelineInfiniteScrollParams): UsePipelineInfiniteScrollResult => {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        // Don't trigger loadMore during initial loading
        if (entry.isIntersecting && hasMore && !isLoading && !isInitialLoading) {
          loadMore();
        }
      },
      { threshold },
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isLoading, isInitialLoading, loadMore, threshold]);

  return { sentinelRef };
};
